import React from 'react';

import TypingTestResultRankingWpmModel from '@Api/models/TypingTestResultRankingWpmModel';

import DateTimeDistanceShort from '@Components/atoms/DateTime/DateTimeDistanceShort';
import Skeleton from '@Components/atoms/Skeleton';
import Text from '@Components/atoms/Text';
import { PropsWithClassName } from '@Components/helper';
import { getDynamicSubRoute } from '@Components/helper/url';
import { getStringWithLength } from '@Components/helper/utils';
import UserLink from '@Components/molecules/UserLink/UserLink';
import { Routes } from '@Components/Routes';

import useRouter from '@Helpers/i18n/useRouter';
import { TextAlign, TextColor, TextSize } from '@Helpers/types/text';

import {
  CreatedAtText,
  PositionText,
  Root,
  ScoreText,
  SkeletonTooltipTrigger,
  StyledTrophyIcon,
  WordsPerMinuteText,
} from './TypingTestHighscoreWpmRank.styles';

interface Props extends PropsWithClassName {
  resultModel: TypingTestResultRankingWpmModel;
  position: number;
}

const TypingTestHighscoreWpmRank = (props: Props): React.ReactElement => {
  const { resultModel, position } = props;

  const router = useRouter();
  const score = Math.round(resultModel.score / 1000);

  const renderPosition = () => {
    const rank = resultModel.rank ?? position;

    if (rank > 3) {
      return (
        <PositionText size={TextSize.Medium} bold color={TextColor.Tertiary}>
          {rank}
        </PositionText>
      );
    }

    return <StyledTrophyIcon rank={rank} />;
  };

  const user = resultModel.user;

  return (
    <Root
      isHighlighted={resultModel.isCurrentUser}
      className={props.className}
      data-testid={props.dataTestId ?? 'typing-test-highscore-wpm-rank-root'}
      onClick={() => user && router.push(getDynamicSubRoute(Routes.User, user.username))}
    >
      {renderPosition()}
      {resultModel.user && <UserLink user={resultModel.user} />}
      <ScoreText size={TextSize.Xs} textAlign={TextAlign.Right} color={TextColor.Tertiary}>
        {score}
      </ScoreText>
      <WordsPerMinuteText bold size={TextSize.Xs} textAlign={TextAlign.Right}>
        {resultModel.wpm}
      </WordsPerMinuteText>
      <CreatedAtText size={TextSize.Xs} textAlign={TextAlign.Right}>
        <DateTimeDistanceShort distanceDate={resultModel.createdAt} />
      </CreatedAtText>
    </Root>
  );
};

const SkeletonComponent = (props: PropsWithClassName): React.ReactElement => {
  return (
    <Root
      isHighlighted={false}
      className={props.className + ' Rank'}
      data-testid={props.dataTestId ?? 'typing-test-highscore-wpm-rank-skeleton-root'}
    >
      <Skeleton>
        <PositionText size={TextSize.Medium}>{getStringWithLength(2)}</PositionText>
      </Skeleton>
      <Skeleton width="30px" height="30px" round />
      <SkeletonTooltipTrigger>
        <Skeleton>
          <Text size={TextSize.Small}>{getStringWithLength(25)}</Text>
        </Skeleton>
      </SkeletonTooltipTrigger>
      <Skeleton>
        <ScoreText bold size={TextSize.Xs} textAlign={TextAlign.Right}>
          {getStringWithLength(2)}
        </ScoreText>
      </Skeleton>
      <Skeleton>
        <WordsPerMinuteText bold size={TextSize.Xs} textAlign={TextAlign.Right}>
          {getStringWithLength(2)}
        </WordsPerMinuteText>
      </Skeleton>
      <Skeleton>
        <CreatedAtText size={TextSize.Xs} textAlign={TextAlign.Right}>
          {getStringWithLength(2)}
        </CreatedAtText>
      </Skeleton>
    </Root>
  );
};

TypingTestHighscoreWpmRank.Skeleton = SkeletonComponent;

export default TypingTestHighscoreWpmRank;
